import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { type QueryMetric, QueryMetricsService, type QueryType } from "@/services/QueryMetricsService";

interface QueryMetricsModalProps {
  open: boolean;
  onClose: () => void;
}

const formatDuration = (ms: number): string => {
  if (ms < 1) {
    return `${(ms * 1000).toFixed(0)}µs`;
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString();
};

const formatQueryName = (name: string): string => {
  if (name.length > 45) {
    return name.substring(0, 45) + "...";
  }
  return name;
};

const formatQuerySummary = (summary: string): string => {
  if (summary.length > 50) {
    return summary.substring(0, 50) + "...";
  }
  return summary;
};

const getQueryTypeColor = (type: QueryType): string => {
  switch (type) {
    case "reactive-select":
      return "#2196f3";
    case "reactive-relational":
      return "#9c27b0";
    case "static":
      return "#ff9800";
    default:
      return "#666";
  }
};

const getQueryTypeLabel = (type: QueryType): string => {
  switch (type) {
    case "reactive-select":
      return "Reactive (Select)";
    case "reactive-relational":
      return "Reactive (Relational)";
    case "static":
      return "Static";
    default:
      return type;
  }
};

type SortColumn =
  | "executions"
  | "totalDuration"
  | "avgDuration"
  | "p50Duration"
  | "minDuration"
  | "maxDuration"
  | "lastExecuted";
type SortDirection = "asc" | "desc";

export default function QueryMetricsModal({ open, onClose }: QueryMetricsModalProps) {
  const [metrics, setMetrics] = useState<QueryMetric[]>([]);
  const [filteredMetrics, setFilteredMetrics] = useState<QueryMetric[]>([]);
  const [queryTypeFilter, setQueryTypeFilter] = useState<QueryType | "all">("all");
  const [queryNameFilter, setQueryNameFilter] = useState<string>("all");
  const [uniqueQueryNames, setUniqueQueryNames] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("executions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statistics, setStatistics] = useState<{
    totalQueries: number;
    totalExecutions: number;
    avgExecutionsPerQuery: number;
    totalDuration: number;
    avgDuration: number;
  } | null>(null);

  const loadMetrics = () => {
    const allMetrics = QueryMetricsService.getMetrics();
    setMetrics(allMetrics);

    const queryNames = Array.from(new Set(allMetrics.map((m) => m.queryName)));
    setUniqueQueryNames(queryNames.sort());
  };

  useEffect(() => {
    if (open) {
      loadMetrics();

      const unsubscribe = QueryMetricsService.subscribe(() => {
        loadMetrics();
      });

      const interval = setInterval(() => {
        loadMetrics();
      }, 1000);

      return () => {
        unsubscribe();
        clearInterval(interval);
      };
    }
  }, [open]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  useEffect(() => {
    let filtered = [...metrics];

    if (queryTypeFilter !== "all") {
      filtered = filtered.filter((m) => m.queryType === queryTypeFilter);
    }

    if (queryNameFilter !== "all") {
      filtered = filtered.filter((m) => m.queryName === queryNameFilter);
    }

    filtered.sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortColumn) {
        case "executions":
          aValue = a.executionCount;
          bValue = b.executionCount;
          break;
        case "totalDuration":
          aValue = a.totalDuration;
          bValue = b.totalDuration;
          break;
        case "avgDuration":
          aValue = a.avgDuration;
          bValue = b.avgDuration;
          break;
        case "p50Duration":
          aValue = a.p50Duration;
          bValue = b.p50Duration;
          break;
        case "minDuration":
          aValue = a.minDuration;
          bValue = b.minDuration;
          break;
        case "maxDuration":
          aValue = a.maxDuration;
          bValue = b.maxDuration;
          break;
        case "lastExecuted":
          aValue = a.lastExecuted;
          bValue = b.lastExecuted;
          break;
        default:
          return 0;
      }

      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    });

    setFilteredMetrics(filtered);

    const stats = QueryMetricsService.getStatistics(queryTypeFilter === "all" ? undefined : queryTypeFilter);
    setStatistics(stats);
  }, [metrics, queryTypeFilter, queryNameFilter, sortColumn, sortDirection]);

  const handleClearMetrics = () => {
    QueryMetricsService.clearMetrics();
    setMetrics([]);
    setFilteredMetrics([]);
    setStatistics(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Query Performance Metrics</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Query Type</InputLabel>
            <Select
              value={queryTypeFilter}
              label="Query Type"
              onChange={(e) => setQueryTypeFilter(e.target.value as QueryType | "all")}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="reactive-select">Reactive (Select)</MenuItem>
              <MenuItem value="reactive-relational">Reactive (Relational)</MenuItem>
              <MenuItem value="static">Static</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Query Name</InputLabel>
            <Select value={queryNameFilter} label="Query Name" onChange={(e) => setQueryNameFilter(e.target.value)}>
              <MenuItem value="all">All Queries</MenuItem>
              {uniqueQueryNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {formatQueryName(name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" color="error" onClick={handleClearMetrics} size="small">
            Clear Metrics
          </Button>
        </Box>

        {statistics && statistics.totalQueries > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              marginBottom: 2,
              padding: 2,
              borderRadius: 1,
              border: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Queries
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {statistics.totalQueries}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Executions
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {statistics.totalExecutions}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Avg Executions/Query
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {statistics.avgExecutionsPerQuery.toFixed(1)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Avg Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.avgDuration)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.totalDuration)}
              </Typography>
            </Box>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small" sx={{ "& .MuiTableCell-root": { padding: "6px 8px" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Query Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Query Summary</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Triggering Tables</TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("executions")}
                >
                  Executions{getSortIcon("executions")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("totalDuration")}
                >
                  Total{getSortIcon("totalDuration")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("avgDuration")}
                >
                  Avg{getSortIcon("avgDuration")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("p50Duration")}
                >
                  P50{getSortIcon("p50Duration")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("minDuration")}
                >
                  Min{getSortIcon("minDuration")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("maxDuration")}
                >
                  Max{getSortIcon("maxDuration")}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", cursor: "pointer", userSelect: "none" }}
                  onClick={() => handleSort("lastExecuted")}
                >
                  Last Executed{getSortIcon("lastExecuted")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMetrics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No query metrics available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMetrics.map((metric) => (
                  <TableRow key={`${metric.queryType}:${metric.queryName}`} hover>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          padding: "1px 6px",
                          borderRadius: "3px",
                          backgroundColor: getQueryTypeColor(metric.queryType),
                          color: "white",
                          fontSize: "9px",
                          fontWeight: "bold",
                        }}
                      >
                        {getQueryTypeLabel(metric.queryType)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "10px" }}>
                        {formatQueryName(metric.queryName)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "9px", color: "#666" }}>
                        {formatQuerySummary(metric.querySummary)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {Object.keys(metric.triggeringTables).length > 0 ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3 }}>
                          {Object.entries(metric.triggeringTables)
                            .sort(([, a], [, b]) => b - a)
                            .map(([table, count]) => (
                              <Box
                                key={table}
                                sx={{
                                  display: "inline-block",
                                  padding: "1px 4px",
                                  borderRadius: "3px",
                                  backgroundColor: "#424242",
                                  color: "#fff",
                                }}
                              >
                                <Typography variant="caption" sx={{ fontSize: "9px", fontFamily: "monospace" }}>
                                  {table} ({count})
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: "9px", color: "#999" }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color:
                            metric.executionCount > 100
                              ? "#d32f2f"
                              : metric.executionCount > 50
                                ? "#ff9800"
                                : "#4caf50",
                        }}
                      >
                        {metric.executionCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          color:
                            metric.totalDuration > 10000
                              ? "#d32f2f"
                              : metric.totalDuration > 5000
                                ? "#ff9800"
                                : "#4caf50",
                        }}
                      >
                        {formatDuration(metric.totalDuration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          color:
                            metric.avgDuration > 1000 ? "#d32f2f" : metric.avgDuration > 500 ? "#ff9800" : "#4caf50",
                        }}
                      >
                        {formatDuration(metric.avgDuration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          color:
                            metric.p50Duration > 1000 ? "#d32f2f" : metric.p50Duration > 500 ? "#ff9800" : "#4caf50",
                        }}
                      >
                        {formatDuration(metric.p50Duration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "10px", color: "#666" }}>
                        {formatDuration(metric.minDuration)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "10px",
                          color: metric.maxDuration > 2000 ? "#d32f2f" : "#666",
                        }}
                      >
                        {formatDuration(metric.maxDuration)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "10px", color: "#666" }}>
                        {formatTimestamp(metric.lastExecuted)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
